import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Calendar, Users, DollarSign, CheckCircle, Clock, Star, ExternalLink, TrendingUp, User, Settings, Tag, ArrowDown, ArrowUp, FileText, Book } from 'lucide-react';
import { admin as adminApi, appointments as appointmentsApi } from '../utils/api';
import { format } from 'date-fns';
import FinancialAdjustmentModal from '../components/FinancialAdjustmentModal';
import CapacityManager from '../components/CapacityManager';
import GroomerCapacityManager from '../components/GroomerCapacityManager';
import BusinessInfoEditor from '../components/BusinessInfoEditor';
import HeroFeaturesEditor from '../components/HeroFeaturesEditor';
import HomepageServicesEditor from '../components/HomepageServicesEditor';
import ServicesEditor from '../components/ServicesEditor';
import SiteBrandingEditor from '../components/SiteBrandingEditor';
import SitePageContentEditor from '../components/SitePageContentEditor';
import TrackingScriptsEditor from '../components/TrackingScriptsEditor';
import SEOSettingsEditor from '../components/SEOSettingsEditor';
import GroomerSpeedManager from '../components/GroomerSpeedManager';
import CommunicationSettingsEditor from '../components/CommunicationSettingsEditor';
import NotesFormatEditor from '../components/NotesFormatEditor';
import ShopClosuresManager from '../components/ShopClosuresManager';
import CalendarAnnotationsManager from '../components/CalendarAnnotationsManager';
import BusinessHoursManager from '../components/BusinessHoursManager';
import PaymentSettingsEditor from '../components/PaymentSettingsEditor';
import SMSSettings from '../components/SMSSettings';
import DataImportManager from '../components/DataImportManager';
import GroomerSchedules from './GroomerSchedules';
import SizeCategoryManager from '../components/SizeCategoryManager';
import AppointmentSettingsEditor from '../components/AppointmentSettingsEditor';
import ClientDocumentsSettingsEditor from '../components/ClientDocumentsSettingsEditor';
import MembershipIdSettings from '../components/MembershipIdSettings';
import GuestBookingSettings from '../components/GuestBookingSettings';
import SeriesManager from '../components/SeriesManager';
import RemindersManager from '../components/RemindersManager';
import ClientIntakeChecklistEditor from '../components/ClientIntakeChecklistEditor';
import ServiceImagesEditor from '../components/ServiceImagesEditor';
import HomepageImagesEditor from '../components/HomepageImagesEditor';
import PasswordResetManager from '../components/PasswordResetManager';
import AdminSecuritySettings from '../components/AdminSecuritySettings';
import UserDocumentation from '../components/UserDocumentation';
import EmployeePermissions from '../components/EmployeePermissions';
import SystemInfoPanel from '../components/SystemInfoPanel';
import UpdatesPanel from '../components/UpdatesPanel';

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [financialModal, setFinancialModal] = useState({ isOpen: false, appointment: null, type: null });

  // Read tab from URL on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Save scroll position before navigating away
  const saveScrollPosition = () => {
    sessionStorage.setItem('adminSettingsScrollY', window.scrollY.toString());
  };

  // Restore scroll position when returning to settings
  useEffect(() => {
    if (activeTab === 'settings') {
      const savedScrollY = sessionStorage.getItem('adminSettingsScrollY');
      if (savedScrollY) {
        // Wait for content to fully render before scrolling
        const scrollToPosition = () => {
          window.scrollTo({
            top: parseInt(savedScrollY),
            behavior: 'instant' // Use instant instead of smooth for exact positioning
          });
          sessionStorage.removeItem('adminSettingsScrollY');
        };
        
        // Try multiple times to ensure content is loaded
        setTimeout(scrollToPosition, 50);
        setTimeout(scrollToPosition, 200);
        setTimeout(scrollToPosition, 500);
      }
    }
  }, [activeTab]);

  const scrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'appointments') {
      loadAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate]);

  const loadDashboardData = async () => {
    try {
      const [statsRes, clientsRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getClients(),
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const response = await adminApi.getAppointments({ date: selectedDate });
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await adminApi.updateAppointmentStatus(appointmentId, { status: newStatus });
      toast.success('Appointment status updated');
      loadAppointments();
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleFinancialAdjustment = async (adjustmentData) => {
    const { amount, reason, type, remove } = adjustmentData;
    const appointmentId = financialModal.appointment.id;

    try {
      if (remove) {
        if (type === 'discount') {
          await appointmentsApi.removeDiscount(appointmentId);
          toast.success('Discount removed');
        } else {
          await appointmentsApi.removeRefund(appointmentId);
          toast.success('Refund removed');
        }
      } else {
        if (type === 'discount') {
          await appointmentsApi.applyDiscount(appointmentId, {
            discount_amount: amount,
            discount_reason: reason
          });
          toast.success('Discount applied successfully');
        } else {
          await appointmentsApi.applyRefund(appointmentId, {
            refund_amount: amount,
            refund_reason: reason
          });
          toast.success('Refund applied successfully');
        }
      }
      loadAppointments();
      loadDashboardData();
    } catch (error) {
      console.error('Financial adjustment error:', error);
      throw error;
    }
  };

  const openFinancialModal = (appointment, type) => {
    setFinancialModal({ isOpen: true, appointment, type });
  };

  const closeFinancialModal = () => {
    setFinancialModal({ isOpen: false, appointment: null, type: null });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="mb-6 flex space-x-2 border-b border-gray-200 overflow-x-auto">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'appointments', label: 'Appointments' },
            { key: 'clients', label: 'Clients' },
            { key: 'stats', label: 'Stats' },
            { key: 'settings', label: 'Settings' },
            { key: 'site-design', label: 'Site Design' },
            { key: 'updates', label: 'Updates' },
            { key: 'documentation', label: 'Documentation', icon: Book },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-6 py-3 font-medium transition-colors flex items-center space-x-2 whitespace-nowrap ${
                activeTab === key
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {Icon && <Icon size={18} />}
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Clients</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_clients}</p>
                  </div>
                  <Users className="text-primary-600" size={40} />
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600">{stats.pending_appointments}</p>
                  </div>
                  <Clock className="text-yellow-600" size={40} />
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Confirmed</p>
                    <p className="text-3xl font-bold text-green-600">{stats.confirmed_appointments}</p>
                  </div>
                  <CheckCircle className="text-green-600" size={40} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h2 className="text-xl font-bold mb-4">Today's Appointments</h2>
                <p className="text-4xl font-bold text-primary-600">{stats.today_appointments}</p>
                <p className="text-gray-600 mt-2">Scheduled for today</p>
              </div>

              <div className="card">
                <h2 className="text-xl font-bold mb-4">Quick Stats</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Pets:</span>
                    <span className="font-semibold">{stats.total_pets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Groomers:</span>
                    <span className="font-semibold">{stats.active_groomers}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div>
            <div className="mb-6 flex items-center space-x-4">
              <label className="font-medium">Filter by Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="input max-w-xs"
              />
            </div>

            {appointments.length === 0 ? (
              <div className="card text-center py-12">
                <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No appointments for this date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((apt) => (
                  <div key={apt.id} className="card">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                          <h3 className="text-lg font-bold">{apt.client_first_name} {apt.client_last_name}</h3>
                          {/* Roadmap item #20: visually distinct tag for
                              guest (no-account) bookings so admins can
                              triage them differently. */}
                          {apt.is_guest ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800">GUEST</span>
                          ) : null}
                          {/* Roadmap item #19: badge so admins can spot
                              which appointments are part of a recurring
                              series at a glance. */}
                          {apt.appointment_series_id ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-800" title="Part of a recurring series">↻ RECURRING</span>
                          ) : null}
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            apt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            apt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            apt.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            apt.status === 'not_completed' ? 'bg-orange-100 text-orange-800' :
                            apt.status === 'no_show' ? 'bg-gray-100 text-gray-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {apt.status === 'no_show' ? 'NO SHOW' : apt.status === 'not_completed' ? 'NOT COMPLETED' : apt.status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div><strong>Pet:</strong> {apt.pet_name || apt.guest_pet_name || '—'} {(apt.pet_size || apt.guest_pet_size) ? `(${apt.pet_size || apt.guest_pet_size})` : ''}</div>
                          <div><strong>Service:</strong> {apt.service_name || 'Standard Appointment'}</div>
                          <div><strong>Time:</strong> {apt.start_time} - {apt.end_time}</div>
                          <div><strong>Phone:</strong> {apt.client_phone}</div>
                          <div><strong>Email:</strong> {apt.client_email}</div>
                        </div>

                        {/* Pricing Info */}
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Original Price:</span>
                            <span className="font-semibold">${parseFloat(apt.total_price || 0).toFixed(2)}</span>
                          </div>
                          {apt.discount_amount > 0 && (
                            <div className="flex justify-between text-orange-600">
                              <span className="flex items-center space-x-1">
                                <Tag size={14} />
                                <span>Discount:</span>
                              </span>
                              <span className="font-semibold">-${parseFloat(apt.discount_amount).toFixed(2)}</span>
                            </div>
                          )}
                          {apt.refund_amount > 0 && (
                            <div className="flex justify-between text-red-600">
                              <span className="flex items-center space-x-1">
                                <DollarSign size={14} />
                                <span>Refund:</span>
                              </span>
                              <span className="font-semibold">-${parseFloat(apt.refund_amount).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t border-gray-200">
                            <span className="font-semibold text-gray-900">Final Price:</span>
                            <span className="font-bold text-primary-600">
                              ${parseFloat(apt.final_price || apt.total_price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Discount/Refund Reasons */}
                        {(apt.discount_reason || apt.refund_reason) && (
                          <div className="mt-2 space-y-1">
                            {apt.discount_reason && (
                              <div className="text-xs text-orange-700 bg-orange-50 p-2 rounded">
                                <strong>Discount Reason:</strong> {apt.discount_reason}
                              </div>
                            )}
                            {apt.refund_reason && (
                              <div className="text-xs text-red-700 bg-red-50 p-2 rounded">
                                <strong>Refund Reason:</strong> {apt.refund_reason}
                              </div>
                            )}
                          </div>
                        )}

                        {apt.groomer_first_name && (
                          <div className="mt-2 text-sm">
                            <strong>Groomer:</strong> {apt.groomer_first_name} {apt.groomer_last_name}
                          </div>
                        )}

                        {apt.special_requests && (
                          <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
                            <strong>Special Requests:</strong> {apt.special_requests}
                          </div>
                        )}
                      </div>

                      {apt.status === 'pending' && (
                        <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col space-y-2">
                          <button
                            onClick={() => handleStatusChange(apt.id, 'confirmed')}
                            className="btn bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => handleStatusChange(apt.id, 'cancelled')}
                            className="btn bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Cancel
                          </button>
                          <div className="pt-2 border-t border-gray-200">
                            <button
                              onClick={() => openFinancialModal(apt, 'discount')}
                              className="btn bg-orange-100 text-orange-700 hover:bg-orange-200 w-full mb-2"
                            >
                              <Tag size={16} className="mr-1" />
                              {apt.discount_amount > 0 ? 'Edit Discount' : 'Add Discount'}
                            </button>
                            <button
                              onClick={() => openFinancialModal(apt, 'refund')}
                              className="btn bg-red-100 text-red-700 hover:bg-red-200 w-full"
                            >
                              <DollarSign size={16} className="mr-1" />
                              {apt.refund_amount > 0 ? 'Edit Refund' : 'Add Refund'}
                            </button>
                          </div>
                        </div>
                      )}

                      {apt.status === 'confirmed' && (
                        <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col space-y-2">
                          <button
                            onClick={() => handleStatusChange(apt.id, 'completed')}
                            className="btn bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            Mark Complete
                          </button>
                          <button
                            onClick={() => handleStatusChange(apt.id, 'no_show')}
                            className="btn bg-gray-100 text-gray-700 hover:bg-gray-200"
                          >
                            No Show
                          </button>
                          <div className="pt-2 border-t border-gray-200">
                            <button
                              onClick={() => openFinancialModal(apt, 'discount')}
                              className="btn bg-orange-100 text-orange-700 hover:bg-orange-200 w-full mb-2"
                            >
                              <Tag size={16} className="mr-1" />
                              {apt.discount_amount > 0 ? 'Edit Discount' : 'Add Discount'}
                            </button>
                            <button
                              onClick={() => openFinancialModal(apt, 'refund')}
                              className="btn bg-red-100 text-red-700 hover:bg-red-200 w-full"
                            >
                              <DollarSign size={16} className="mr-1" />
                              {apt.refund_amount > 0 ? 'Edit Refund' : 'Add Refund'}
                            </button>
                          </div>
                        </div>
                      )}

                      {(apt.status === 'completed' || apt.status === 'cancelled' || apt.status === 'no_show' || apt.status === 'not_completed') && (
                        <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col space-y-2">
                          <button
                            onClick={() => openFinancialModal(apt, 'discount')}
                            className="btn bg-orange-100 text-orange-700 hover:bg-orange-200 w-full"
                          >
                            <Tag size={16} className="mr-1" />
                            {apt.discount_amount > 0 ? 'Edit Discount' : 'Add Discount'}
                          </button>
                          <button
                            onClick={() => openFinancialModal(apt, 'refund')}
                            className="btn bg-red-100 text-red-700 hover:bg-red-200 w-full"
                          >
                            <DollarSign size={16} className="mr-1" />
                            {apt.refund_amount > 0 ? 'Edit Refund' : 'Add Refund'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="relative">
            {/* Scroll to Bottom Button */}
            <button
              onClick={scrollToBottom}
              className="fixed right-8 top-32 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDown size={20} />
            </button>

            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Phone</th>
                    <th className="text-left py-3 px-4">Pets</th>
                    <th className="text-left py-3 px-4">Appointments</th>
                    <th className="text-left py-3 px-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{client.first_name} {client.last_name}</td>
                      <td className="py-3 px-4">{client.email}</td>
                      <td className="py-3 px-4">{client.phone}</td>
                      <td className="py-3 px-4">{client.pet_count}</td>
                      <td className="py-3 px-4">{client.appointment_count}</td>
                      <td className="py-3 px-4">{format(new Date(client.created_at), 'MMM d, yyyy')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Scroll to Top Button */}
            <button
              onClick={scrollToTop}
              className="fixed right-8 bottom-8 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to top"
            >
              <ArrowUp size={20} />
            </button>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Statistics & Analytics</h2>
                <p className="text-gray-600">View detailed earnings, revenue, and inventory management</p>
              </div>
              <Link
                to="/stats"
                className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <TrendingUp size={20} />
                <span>View Full Stats</span>
                <ExternalLink size={16} />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-green-900">Earnings & Revenue</h3>
                  <DollarSign className="text-green-600" size={32} />
                </div>
                <p className="text-gray-700 mb-4">
                  Track weekly, monthly, and yearly earnings with detailed breakdowns by service and client.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Net revenue calculations</li>
                  <li>• Discount tracking</li>
                  <li>• Refund management</li>
                  <li>• Revenue by service</li>
                  <li>• Top clients analysis</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-blue-900">Inventory Management</h3>
                  <Star className="text-blue-600" size={32} />
                </div>
                <p className="text-gray-700 mb-4">
                  Keep track of grooming supplies, products, and equipment with low stock alerts.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Add/edit/delete items</li>
                  <li>• Track quantities</li>
                  <li>• Cost per unit</li>
                  <li>• Low stock alerts</li>
                  <li>• Category organization</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-purple-900">Financial Tools</h3>
                  <CheckCircle className="text-purple-600" size={32} />
                </div>
                <p className="text-gray-700 mb-4">
                  Manage discounts and refunds for appointments with full tracking and reporting.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Apply discounts</li>
                  <li>• Issue refunds</li>
                  <li>• Track reasons</li>
                  <li>• Final price calculation</li>
                  <li>• Financial reporting</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Site Design Tab */}
        {activeTab === 'site-design' && (
          <div className="space-y-6 relative">
            {/* Scroll to Bottom Button */}
            <button
              onClick={scrollToBottom}
              className="fixed right-8 top-32 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDown size={20} />
            </button>

            {/* Site Branding & Navigation */}
            <SiteBrandingEditor />

            {/* About Us / Contact Us page content (markdown + images) */}
            <SitePageContentEditor />

            {/* SEO foundation — site title, meta description, OG image,
                business type, indexing toggle. Drives sitemap.xml + JSON-LD. */}
            <SEOSettingsEditor />

            {/* GA4 / Meta Pixel / Google Search Console verification paste-in */}
            <TrackingScriptsEditor />

            {/* Hero Section and Features */}
            <HeroFeaturesEditor />

            {/* Homepage Services Display (Marketing Text) */}
            <HomepageServicesEditor />

            {/* Homepage Images */}
            <HomepageImagesEditor />

            {/* Service & Add-on Images (Client Booking) */}
            <ServiceImagesEditor />

            {/* Business Hours Info (Homepage Display) */}
            <BusinessInfoEditor />

            {/* Scroll to Top Button */}
            <button
              onClick={scrollToTop}
              className="fixed right-8 bottom-8 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to top"
            >
              <ArrowUp size={20} />
            </button>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6 relative">
            {/* Scroll to Bottom Button */}
            <button
              onClick={scrollToBottom}
              className="fixed right-8 top-32 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDown size={20} />
            </button>
            {/* Bookable Services and Add-ons (Actual Booking System) */}
            <ServicesEditor />

            {/* Size Categories & Pricing */}
            <SizeCategoryManager />

            {/* Appointment Settings */}
            <AppointmentSettingsEditor />

            {/* Client Document Settings */}
            <ClientDocumentsSettingsEditor />

            {/* Client Intake Checklist (auto-spawned document requests on signup) */}
            <ClientIntakeChecklistEditor />

            {/* Membership IDs (roadmap item #21) */}
            <MembershipIdSettings />

            {/* Guest Booking (roadmap item #20) */}
            <GuestBookingSettings />

            {/* Recurring Appointments (roadmap item #19 Phase 1) */}
            <SeriesManager />

            {/* Recurring Reminders (roadmap item #19 Phase 2) */}
            <RemindersManager />

            {/* Business Operating Hours (Actual Hours) */}
            <BusinessHoursManager />

            {/* Groomer Schedules (Booking Availability) */}
            <GroomerSchedules />

            {/* Shop Closure Dates */}
            <ShopClosuresManager />

            {/* Calendar Annotations (roadmap item #18) */}
            <CalendarAnnotationsManager />

            {/* Groomer Service Speeds */}
            <GroomerSpeedManager />

            {/* System-wide Capacity Management */}
            <CapacityManager />

            {/* Groomer-specific Capacity Management */}
            <GroomerCapacityManager />

            {/* Notes format (free-form / SOAP / custom labels) */}
            <NotesFormatEditor />

            {/* Email & SMS Settings */}
            <CommunicationSettingsEditor />

            {/* SMS Settings */}
            <SMSSettings />

            {/* Payment Settings */}
            <PaymentSettingsEditor />

            {/* Data Import/Export */}
            <DataImportManager />

            {/* Password Reset Requests */}
            <PasswordResetManager />

            {/* Admin Security Settings */}
            <AdminSecuritySettings />

            {/* Employee Permissions */}
            <EmployeePermissions />

            {/* System Info & Updates */}
            <SystemInfoPanel />

            {/* Other Settings */}
            <div className="card">
              <h2 className="text-2xl font-bold mb-6">Other Settings</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2 flex items-center space-x-2">
                    <User size={20} className="text-primary-600" />
                    <span>Client Form Fields</span>
                  </h3>
                  <p className="text-gray-600 mb-3">Customize registration and profile form fields - add, edit, or remove fields to match your business needs</p>
                  <Link
                    to="/form-fields"
                    onClick={saveScrollPosition}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <span>Manage Form Fields</span>
                  </Link>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2 flex items-center space-x-2">
                    <Settings size={20} className="text-primary-600" />
                    <span>Service Subjects (Pets/Lawns/Vehicles)</span>
                  </h3>
                  <p className="text-gray-600 mb-3">Configure what your business services - customize fields for pets, lawns, vehicles, or disable subjects entirely</p>
                  <Link
                    to="/subject-manager"
                    onClick={saveScrollPosition}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <span>Manage Service Subjects</span>
                  </Link>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2 flex items-center space-x-2">
                    <Star size={20} className="text-primary-600" />
                    <span>Business Recommendations</span>
                  </h3>
                  <p className="text-gray-600 mb-3">Manage recommended businesses and services that clients can view (vets, daycares, pet stores, etc.)</p>
                  <Link
                    to="/recommendations-manager"
                    onClick={saveScrollPosition}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <span>Manage Recommendations</span>
                    <ExternalLink size={16} />
                  </Link>
                </div>

                <div>
                  <h3 className="font-semibold mb-2 flex items-center space-x-2">
                    <FileText size={20} className="text-primary-600" />
                    <span>Invoice Templates</span>
                  </h3>
                  <p className="text-gray-600 mb-3">Customize invoice and receipt PDFs — logo, accent color, custom client questions, provider info, insurance-ready service codes. Multiple templates can be created and one set as the default.</p>
                  <Link
                    to="/invoice-templates"
                    onClick={saveScrollPosition}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <span>Manage Invoice Templates</span>
                    <ExternalLink size={16} />
                  </Link>
                </div>

                <div>
                  <h3 className="font-semibold mb-2 flex items-center space-x-2">
                    <FileText size={20} className="text-primary-600" />
                    <span>Forms</span>
                  </h3>
                  <p className="text-gray-600 mb-3">Build reusable intake, consent, and waiver forms — vaccination records, allergy intake, PAR-Q, tattoo consent, etc. Each form gets a tokenized share link the client fills out without needing an account; submissions are stored against the customer record.</p>
                  <Link
                    to="/forms"
                    onClick={saveScrollPosition}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <span>Manage Forms</span>
                    <ExternalLink size={16} />
                  </Link>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold mb-2">Import Client Data</h3>
                  <p className="text-gray-600 mb-3">Use the API endpoint /api/admin/import-clients to import existing client files.</p>
                  <p className="text-sm text-gray-500">Format: JSON array with client data including email, firstName, lastName, phone, and optional pets array.</p>
                </div>
              </div>
            </div>

            {/* Scroll to Top Button */}
            <button
              onClick={scrollToTop}
              className="fixed right-8 bottom-8 z-10 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
              title="Scroll to top"
            >
              <ArrowUp size={20} />
            </button>
          </div>
        )}

        {/* Updates Tab */}
        {activeTab === 'updates' && (
          <UpdatesPanel />
        )}

        {/* Documentation Tab */}
        {activeTab === 'documentation' && (
          <UserDocumentation />
        )}

        {/* Financial Adjustment Modal */}
        <FinancialAdjustmentModal
          isOpen={financialModal.isOpen}
          onClose={closeFinancialModal}
          appointment={financialModal.appointment}
          onSave={handleFinancialAdjustment}
          type={financialModal.type}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
